import React, { useState, useEffect, useMemo } from 'react';
import styles from './basketPage.module.css';

import { Header } from '../../components/Header/Header';
import { SidesItem } from '../../components/SidesItem/SidesItem';
import { DataInput } from '../../UI/DataInput/DataInput';
import { BlueBtn } from '../../UI/BlueBtn/BlueBtn';
import { useGetUserBasketQuery, useDeleteItemFromBasketMutation } from '../../store/api/basket.api';
import { useGetUserByIdQuery } from '../../store/api/user.api';
import { useActivatePromoMutation } from '../../store/api/promo.api';
import { useCalculateTopUpMutation } from '../../store/api/topup.api';
import { useDbUserId } from '../../hooks/useDbUserId';
import { useSelector } from 'react-redux';
import { useUserDataBlurHandler } from '../../hooks/useUserDataBlurHandler.js';

import basketIcon from '../../assets/basket-icon.svg';
import checkboxIcon from '../../assets/checkbox-icon.svg';
import checkboxActiveIcon from '../../assets/checkboxActive-icon.svg';
import plusIcon from '../../assets/inputPlus-icon.svg';

export const BasketPage = () => {
	const schema = window.sessionStorage.getItem('schema');

	const forNewAccount = Boolean(schema);
	const paymentMethod = 'sbp';
	const [promo, setPromo] = useState({
		id: null,
		value: '',
		disc: null,
		success: false,
		message: '',
	})
	const [checkbox, setCheckbox] = useState(true);

	const { dbUserId } = useDbUserId();
	const { data, refetch } = useGetUserBasketQuery({userId: dbUserId, schema});
	const { id } = useSelector((state) => state.user.user);
	const { data: userData, refetch: userRefetch } = useGetUserByIdQuery(id);

	const [activatePromo] = useActivatePromoMutation();
	const [deleteBasketItem] = useDeleteItemFromBasketMutation();
	const [calculateTopUpServer, { isLoading: isTopUpQuoteLoading }] = useCalculateTopUpMutation();

	const basket = useMemo(() => data?.items || [], [data?.items]);

	const [userDataState, setUserDataState] = useState({
		email: userData?.email || '',
		accountEmail: userData?.accountEmail || '',
		accountPassword: userData?.accountPassword || '',
	});
	const FA = '';
	
	const [topUpQuote, setTopUpQuote] = useState(null);
	const [topUpQuoteError, setTopUpQuoteError] = useState('');
	const [topUpNotice, setTopUpNotice] = useState('');

	const { handleBlur } = useUserDataBlurHandler({
		checkbox,
		dbUserId,
		userRefetch,
		setUserDataState
	});

	
	useEffect(() => {
		if (userData) {
			setUserDataState({
				email: userData.email || '',
				accountEmail: userData.accountEmail || '',
				accountPassword: userData.accountPassword || '',
			});
		}
	}, [userData]);

	const handleDelete = async (basketItem) => {
		try {
			const data = {
				userId: dbUserId,
				itemId: basketItem.itemId || null,
				subscriptionId: basketItem.subscriptionId || null,
				editionId: basketItem.editionId || null,
			};

			await deleteBasketItem({data, schema}).unwrap();
			refetch();
		} catch (error) {
			console.error('Ошибка при удалении из корзины', error);
		}
	};

	const calculatePrice = (basket) => {
		return basket.reduce((sum, item) => {
			if (item.edition){
				return sum + (item.edition?.price ?? 0) * item.quantity;
			} else if (item.itemId) {
				if(schema === 'PS_sharing'){

					const typePrice = `${item.type}_price`;
					return sum + (item.item[typePrice] ?? 0) * item.quantity;

				}
				return sum + (item.item?.price ?? 0) * item.quantity;
			} else if (item.subscriptionId) {
				return sum + (item.subscription?.price ?? 0) * item.quantity;
			}
			return sum;
		}, 0).toFixed(2);
	};

	const calculateDiscPrice = (basket) => {
		return basket.reduce((sum, item) => {
			let price = 0;

			//для издания товара
			if (item.edition){
				price = item.edition?.price_disc ?? item.edition?.price ?? 0;
			}

			// Для товара с Standard
			else if (item.itemId) {
				if(schema === 'PS_sharing'){
					const typePrice = `${item.type}_price`;
					const typePriceDisc = `${item.type}_price_disc`;

					price = item.item[typePriceDisc] ?? item.item[typePrice] ?? 0;
				} else {
					price = item.item?.price_disc_sub ?? item.item?.price_disc ?? item.item?.price ?? 0;
				}
			}
			
			// Для подписки
			else if (item.subscriptionId) {
				price = item.subscription?.price_disc_sub ?? item.subscription?.price_disc ?? item.subscription?.price ?? 0;
			}

			// Если цена существует и скидка применима
			if (price !== 0) {
				return sum + price * item.quantity;
			}

			return sum;
		}, 0);
	};

	const calPrice = calculatePrice(basket);
	let calDiscPrice = calculateDiscPrice(basket);
	if(promo.success){
		calDiscPrice -= calDiscPrice / 100 * Number(promo.disc)
	}
	const discountPercentage = Math.round(((calPrice - calDiscPrice) / calPrice) * 100);

	// Для основной корзины источником суммы, курса, номиналов и цены является только сервер.
	useEffect(() => {
		if (schema !== '' || basket.length === 0) {
			setTopUpQuote(null);
			setTopUpQuoteError('');
			setTopUpNotice('');
			return undefined;
		}

		let ignoreResult = false;
		setTopUpQuote(null);
		setTopUpQuoteError('');
		setTopUpNotice('');

		calculateTopUpServer({ promoCodeId: promo.success ? promo.id : null })
			.unwrap()
			.then(result => {
				if (!ignoreResult) setTopUpQuote(result);
			})
			.catch(error => {
				if (!ignoreResult) {
					console.error('Ошибка при расчёте пополнения:', error);
					setTopUpQuoteError(error?.data?.message || 'Не удалось рассчитать заказ');
				}
			});

		return () => {
			ignoreResult = true;
		};
	}, [basket, promo.id, promo.success, schema, calculateTopUpServer]);

	const editionIds = basket
		.filter(item => item.editionId !== null)
		.flatMap(item => Array(item.quantity).fill(item.editionId));

	const itemIds = basket
		.filter(item => item.itemId !== null)
		.flatMap(item => Array(item.quantity).fill(item.itemId));

	const subscriptionIds = basket
		.filter(item => item.subscriptionId !== null)
		.flatMap(item => Array(item.quantity).fill(item.subscriptionId));

	
	const itemsData = []
	basket.forEach(item => {
		itemsData.push({
			id: item?.item?.id,
			type: item?.type,
			quantity: item?.quantity
		})
	})
	
	// Для основного раздела игр (schema==='') формируем данные для заказа пополнения кошелька
	// Для остальных разделов — обычный заказ
	const orderData = schema === '' ? {
		isTopUpOrder: true,
		email: userDataState.email,
		promoCodeId: promo.success ? promo.id : null,
		quoteId: topUpQuote?.quoteId,
		onQuoteStale: freshQuote => {
			setTopUpQuote(freshQuote);
			setTopUpQuoteError('');
			setTopUpNotice('Расчёт изменился. Проверьте новые значения и нажмите «Оплатить» ещё раз.');
		},
	} : {
		price: calDiscPrice,
		userId: dbUserId,
		inShopInfo: {
			email: userDataState.email,
			login: userDataState.accountEmail,
			password: userDataState.accountPassword,
			FA: FA
		},
		itemIds,
		forNewAccount,
		itemsData,
		subscriptionIds,
		editionIds,
		paymentMethod,
		promoCodeId: promo.success ? promo.id : null,
	};

	const handleInputChange = (fieldName, value) => {
		setUserDataState(prevState => ({
			...prevState,
			[fieldName]: value,
		}));
	};

	const handlePromoBlur = async (value) =>{
		if(value.length > 0){
			const res = await activatePromo({ code: value })
			const resMess = res?.error ? res?.error.data.message : res?.data.message;
			setPromo({
				...promo,
				id: res?.data?.promoId || null,
				disc: res?.data?.disc || null,
				success: res?.error ? false : true,
				message: resMess
			})
		}
	}

	const minPrice = 200;
	const topUpQuoteReady = Boolean(topUpQuote?.quoteId && topUpQuote?.totalPriceRub > 0);
	const displayedPrice = schema === '' ? topUpQuote?.totalPriceRub : calDiscPrice;

	return (
		<div className={'main'}>
			<Header />
			<div className={styles.header}>	
				<div className={styles.title_block}>
					<img src={basketIcon} alt="basketIcon" />
					<h2>Корзина</h2>
				</div>
				<p>{basket.length} товара</p>
			</div>
			{basket.length === 0 ? (
				<h1>Список пуст</h1>
			) : (
				<>
					<div className={styles.items_block}>
							{basket.map((item, index) => (
								<React.Fragment key={item.id || index}>
									<SidesItem
										item={item.edition ? {
											...item.edition.item,
											editionTitle: item.edition.title,
											edition: true,
											id: item.edition.id,
											price: item.edition.price,
											price_disc: item.edition.price_disc,
											price_disc_sub: item.edition.price_disc,
										} : item.itemId ? item.item : item.subscription}
										itemType={item.type && item.type}
										onDelete={() => handleDelete(item)}
										from={'Basket'}
									/>
									{index !== basket.length - 1 && <div className={styles.line}></div>}
								</React.Fragment>
							))}
					</div>
					
					{
						schema === '' ?
						<>
							{/* Блок пополнения кошелька для основного раздела игр */}
							<div className="form">
								<p>Пополнение кошелька PlayStation</p>
								{isTopUpQuoteLoading ? (
									<p style={{ marginTop: 10, color: '#888' }}>Рассчитываем актуальные номиналы и цену…</p>
								) : topUpQuote ? (
									<>
										<p style={{ marginTop: 10, fontSize: 14, color: '#888' }}>
											Стоимость игр в корзине {topUpQuote.sourcePriceRub} ₽ делится на курс {topUpQuote.rateRubPerTl} ₽/TL
											 ({topUpQuote.amountTL} TL). После оплаты вы получите код пополнения кошелька PlayStation
											 на {topUpQuote.total} TL с инструкцией по активации.
										</p>
										{topUpNotice && <p style={{ marginTop: 10, color: '#ffb020' }}>{topUpNotice}</p>}
										<div style={{ marginTop: 15 }}>
											<p style={{ fontWeight: 600, marginBottom: 10 }}>Выбранные карты пополнения:</p>
											{topUpQuote.combo.map((item, index) => (
												<div key={`${item.itemId}-${index}`} style={{
													padding: '12px 14px',
													marginBottom: '8px',
													background: 'linear-gradient(135deg, #0050EF 0%, #0037A8 100%)',
													borderRadius: '14px',
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
													boxShadow: '0 4px 12px rgba(0, 80, 239, 0.25)'
												}}>
													<span style={{ color: '#fff', fontWeight: 500 }}>Номинал {item.nominal} TL</span>
													<span style={{ color: '#fff', fontWeight: 600 }}>1 шт</span>
												</div>
											))}
										</div>
										{/* Кнопка "У меня есть вопрос" */}
										<a
											href="https://t.me/eazysupp"
											target="_blank"
											rel="noopener noreferrer"
											style={{
												display: 'block',
												marginTop: 15,
												padding: '12px',
												background: '#0050EF',
												color: '#fff',
												textAlign: 'center',
												borderRadius: '14px',
												textDecoration: 'none',
												fontWeight: 500
											}}
										>
											У меня есть вопрос
										</a>
									</>
								) : (
									<p style={{ marginTop: 10, color: '#ff4444' }}>
										{topUpQuoteError || 'Не удалось получить актуальный расчёт'}
									</p>
								)}
							</div>
	
							<div className="form" style={{marginTop: '15px'}}>
								<DataInput
									name="email"
									text={'Почта для чека'}
									img={plusIcon}
									type={'email'}
									value={userDataState.email}
									onChange={value => handleInputChange("email", value)}
									onBlur={value => handleBlur("email", value)}
								/>
								<div className={styles.formLine}></div>
								<div className={styles.checkbox} onClick={() => setCheckbox(!checkbox)}>
									{checkbox ? (
										<img src={checkboxActiveIcon} alt="checkboxActiveIcon" />
									) : (
										<img src={checkboxIcon} alt="checkboxIcon" />
									)}
									<span>Запомнить данные</span>
								</div>
							</div>
						</>
						:
						<div className="form" style={{marginTop: '0'}}>
							<DataInput
								name="email"
								text={'Почта для чека'}
								img={plusIcon}
								type={'email'}
								value={userDataState.email}
								onChange={value => handleInputChange("email", value)}
								onBlur={value => handleBlur("email", value)}
							/>
							<div className={styles.formLine}></div>
							<div className={styles.checkbox} onClick={() => setCheckbox(!checkbox)}>
								{checkbox ? (
									<img src={checkboxActiveIcon} alt="checkboxActiveIcon" />
								) : (
									<img src={checkboxIcon} alt="checkboxIcon" />
								)}
								<span>Запомнить данные</span>
							</div>
						</div>
					}


					<div className="form">
						<p>Используйте промокод</p>
						{
							promo.message.length > 0 &&
								<p style={promo.success ? {marginTop: 10, color: "green"} : {marginTop: 10, color: "red"}}>{promo.message}</p>
						}
						
						<DataInput
							name="promo"
							text={'Промокод'}
							img={plusIcon}
							type={'text'}
							value={promo.value}
							onChange={value => setPromo({...promo, value})}
							onBlur={value => handlePromoBlur(value)}
						/>
					</div>

					<div className={`form ${styles.payForm}`}>
						<p className={styles.pay_title}>Сумма товаров в корзине</p>
						<div className={styles.price_block}>
							{
								schema !== '' && discountPercentage !== 0 &&
									<span className={styles.price_amount}>
										{calPrice} ₽
									</span>
							}
						</div>
							<h2 style={schema !== '' && discountPercentage !== 0 ? {color: 'red'} : {}}>
								{displayedPrice ?? '—'} ₽
							</h2>
							{
								schema === '' ? (
									topUpQuoteReady ?
										<BlueBtn
											text={`Оплатить`}
											pay={true}
											type={'pay'}
											orderData={orderData}
										/>
										: <span className={styles.minSum_text}>Оплата станет доступна после успешного расчёта</span>
								) : calDiscPrice < minPrice ? (
									<span className={styles.minSum_text}>Минимальная сумма заказа {minPrice}!</span>
								) : (
									<BlueBtn
										text={`Оплатить`}
										pay={true}
										type={'pay'}
										orderData={orderData}
									/>
								)
						  }
						
					</div>
				</>
			)}
		</div>
	);
};
